import { HStack, Icon } from "@chakra-ui/react";
import { useCallback, useState, useEffect, useMemo } from "react";
import { MdErrorOutline } from "react-icons/md";
import WeekNavigator from "features/MeetingEvents/Components/WeekNavigator";
import { useCurrentUserQuery, useUserListQuery } from "features/User/hooks";
import TableFilterSelect from "components/Select/TableFilterSelect";
import FilterButton from "components/Button/FilterButton";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import { COMMS_STATUSES, STAGE_OPTIONS } from "features/Resources/Shared/constants";
import { TabOption, TabSelector } from "components/TabSelector";

export type ViewMode = "week" | "all";
export type SortMode = "effective_deliver_at_asc" | "effective_deliver_at_desc";

export interface MessagesFilters {
  q?: string;
  sender_id?: string;
  recipient_id?: string;
  recipient_type?: string;
  start_date?: string;
  end_date?: string;
  view_mode?: ViewMode;
  has_issues?: string;
  status?: string;
  stage_key?: string;
  sort?: SortMode;
}

const DEFAULT_MESSAGE_SORT = "effective_deliver_at_desc";
const DEFAULT_MESSAGE_SORT_OPTIONS: SortOption[] = [
  { label: "Deliver At: Oldest First", value: "effective_deliver_at_asc" },
  { label: "Deliver At: Newest First", value: "effective_deliver_at_desc" },
];

interface MessagesFiltersProps {
  filters: MessagesFilters;
  onFiltersChange: (filters: MessagesFilters) => void;
  onReset: () => void;
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
  sortOptions?: SortOption[];
}

type FilterOption = { label: string; value: string };
type SenderOption = FilterOption;
type StatusOption = FilterOption;
type StageOption = FilterOption;

function FilterTabs({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TabSelector value={value} onChange={onChange}>
      {options.map((option) => (
        <TabOption key={option.value} value={option.value}>
          {option.label}
        </TabOption>
      ))}
    </TabSelector>
  );
}

const viewModeOptions = [
  { label: "View by Week", value: "week" },
  { label: "View All", value: "all" },
];

const statusOptions = Array.from(COMMS_STATUSES.values()).map((status) => ({
  label: status.label,
  value: status.value,
}));

export default function MessagesFiltersComponent({
  filters,
  onFiltersChange,
  onReset,
  currentSort = DEFAULT_MESSAGE_SORT,
  defaultSortString = DEFAULT_MESSAGE_SORT,
  onSortChange: onSortChangeProp,
  sortOptions = DEFAULT_MESSAGE_SORT_OPTIONS,
}: MessagesFiltersProps) {
  const [localSort, setLocalSort] = useState(DEFAULT_MESSAGE_SORT);
  const onSortChange = onSortChangeProp || setLocalSort;
  const effectiveSort = onSortChangeProp ? currentSort : localSort;
  const { data: currentUser } = useCurrentUserQuery();
  const { data: allUsers = [] } = useUserListQuery();
  const [selectedSender, setSelectedSender] = useState<SenderOption | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<SenderOption | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<StatusOption | null>(null);
  const [selectedStage, setSelectedStage] = useState<StageOption | null>(null);

  const allSenderOptions = useMemo(() => {
    const userOptions = allUsers.map((user) => ({
      label: user.fullName,
      value: user.id,
    }));
    return [{ label: "Camino Bot", value: "null" }, ...userOptions];
  }, [allUsers]);

  const allRecipientOptions = useMemo(() => {
    return allUsers.map((user) => ({
      label: user.fullName,
      value: user.id,
    }));
  }, [allUsers]);

  // Check if "My Messages" filter is active
  const isMyMessagesActive = filters.sender_id === currentUser?.id;

  // Sync selectedSender when filters.sender_id changes (e.g., from URL params on mount)
  useEffect(() => {
    if (filters.sender_id && !selectedSender && allSenderOptions.length > 0) {
      const matchingSender = allSenderOptions.find((option) => option.value === filters.sender_id);
      if (matchingSender) {
        setSelectedSender(matchingSender);
      }
    } else if (!filters.sender_id && selectedSender) {
      setSelectedSender(null);
    }
  }, [filters.sender_id, allSenderOptions, selectedSender]);

  // Sync selectedRecipient when filters.recipient_id changes
  useEffect(() => {
    if (filters.recipient_id && !selectedRecipient && allRecipientOptions.length > 0) {
      const matchingRecipient = allRecipientOptions.find((option) => option.value === filters.recipient_id);
      if (matchingRecipient) {
        setSelectedRecipient(matchingRecipient);
      }
    } else if (!filters.recipient_id && selectedRecipient) {
      setSelectedRecipient(null);
    }
  }, [filters.recipient_id, allRecipientOptions, selectedRecipient]);

  // Sync selectedStatus when filters.status changes
  useEffect(() => {
    if (filters.status && !selectedStatus) {
      const matchingStatus = statusOptions.find((option) => option.value === filters.status);
      if (matchingStatus) {
        setSelectedStatus(matchingStatus);
      }
    } else if (!filters.status && selectedStatus) {
      setSelectedStatus(null);
    }
  }, [filters.status, selectedStatus]);

  // Sync selectedStage when filters.stage_key changes
  useEffect(() => {
    if (filters.stage_key && !selectedStage) {
      const matchingStage = STAGE_OPTIONS.find((option) => option.value === filters.stage_key);
      if (matchingStage) {
        setSelectedStage(matchingStage);
      }
    } else if (!filters.stage_key && selectedStage) {
      setSelectedStage(null);
    }
  }, [filters.stage_key, selectedStage]);

  const handleFilterChange = useCallback(
    (key: keyof MessagesFilters, value: string | undefined) => {
      const newValue = value === "" ? undefined : value;
      if (filters[key] !== newValue) {
        const updatedFilters = {
          ...filters,
          [key]: newValue,
        };

        // When setting recipient_id, also set recipient_type
        if (key === "recipient_id") {
          updatedFilters.recipient_type = newValue ? "User" : undefined;
        }

        onFiltersChange(updatedFilters);
      }
    },
    [filters, onFiltersChange],
  );

  const loadSenderOptions = async (inputValue: string): Promise<SenderOption[]> => {
    if (!inputValue || inputValue.length < 2) {
      return allSenderOptions;
    }

    const filtered = allSenderOptions.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()));
    return filtered;
  };

  const loadRecipientOptions = async (inputValue: string): Promise<SenderOption[]> => {
    if (!inputValue || inputValue.length < 2) {
      return allRecipientOptions;
    }

    const filtered = allRecipientOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
    return filtered;
  };

  const handleWeekChange = useCallback(
    (startDate: string, endDate: string) => {
      onFiltersChange({
        ...filters,
        start_date: startDate,
        end_date: endDate,
      });
    },
    [filters, onFiltersChange],
  );

  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) =>
      key !== "start_date" && key !== "end_date" && key !== "view_mode" && value !== undefined && value !== "",
  );

  const isWithIssuesActive = filters.has_issues === "true";

  const handleWithIssuesToggle = useCallback(() => {
    if (isWithIssuesActive) {
      handleFilterChange("has_issues", undefined);
    } else {
      handleFilterChange("has_issues", "true");
    }
  }, [isWithIssuesActive, handleFilterChange]);

  const handleMyMessagesToggle = useCallback(() => {
    if (isMyMessagesActive) {
      // Deactivate - clear sender filter
      setSelectedSender(null);
      handleFilterChange("sender_id", undefined);
    } else if (currentUser) {
      // Activate - set to current user
      setSelectedSender({ label: currentUser.fullName, value: currentUser.id });
      handleFilterChange("sender_id", currentUser.id);
    }
  }, [isMyMessagesActive, currentUser, handleFilterChange]);

  const handleResetFilters = useCallback(() => {
    setSelectedSender(null);
    setSelectedRecipient(null);
    setSelectedStatus(null);
    setSelectedStage(null);
    onReset();
  }, [onReset]);

  // Build active filters for display
  const activeFilters: ActiveFilter[] = [];

  if (filters.sender_id) {
    activeFilters.push({
      key: "sender",
      label: `Sender: ${isMyMessagesActive ? currentUser?.fullName || "Unknown" : selectedSender?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedSender(null);
        handleFilterChange("sender_id", undefined);
      },
    });
  }

  if (filters.recipient_id) {
    activeFilters.push({
      key: "recipient",
      label: `Recipient: ${selectedRecipient?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedRecipient(null);
        handleFilterChange("recipient_id", undefined);
      },
    });
  }

  if (filters.status) {
    activeFilters.push({
      key: "status",
      label: `Status: ${selectedStatus?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedStatus(null);
        handleFilterChange("status", undefined);
      },
    });
  }

  if (filters.stage_key) {
    activeFilters.push({
      key: "stage",
      label: `Stage: ${selectedStage?.label || "Unknown"}`,
      onRemove: () => {
        setSelectedStage(null);
        handleFilterChange("stage_key", undefined);
      },
    });
  }

  if (filters.has_issues) {
    activeFilters.push({
      key: "has_issues",
      label: "With Issues",
      onRemove: () => handleFilterChange("has_issues", undefined),
    });
  }

  return (
    <>
      <HStack justifyContent="space-between" flexWrap="wrap">
        <FilterTabs
          options={viewModeOptions}
          value={filters.view_mode || "week"}
          onChange={(value) => handleFilterChange("view_mode", value as ViewMode)}
        />

        {filters.view_mode === "week" && (
          <WeekNavigator startTime={filters.start_date} endTime={filters.end_date} onWeekChange={handleWeekChange} />
        )}
      </HStack>
      <FilterSortSearchBar
        searchPlaceholder="Search by message name or recipient..."
        searchValue={filters.q}
        onSearchChange={(value) => handleFilterChange("q", value)}
        currentSort={effectiveSort}
        defaultSort={defaultSortString}
        onSortChange={onSortChange}
        sortOptions={sortOptions}
        quickFilters={
          <>
            <FilterButton onClick={handleMyMessagesToggle} isActive={isMyMessagesActive} size="sm">
              Messages from Me
            </FilterButton>
            <FilterButton
              onClick={handleWithIssuesToggle}
              isActive={isWithIssuesActive}
              icon={<Icon as={MdErrorOutline} color="statusRed.text" boxSize={4} />}
              size="sm"
            >
              With Issues
            </FilterButton>
          </>
        }
        allFilters={
          <>
            <FilterButton onClick={handleMyMessagesToggle} isActive={isMyMessagesActive}>
              Messages from Me
            </FilterButton>
            <FilterButton
              onClick={handleWithIssuesToggle}
              isActive={isWithIssuesActive}
              icon={<Icon as={MdErrorOutline} boxSize={4} />}
            >
              With Issues
            </FilterButton>
            <TableFilterSelect
              placeholder="Filter by status"
              value={selectedStatus}
              onChange={(option) => {
                setSelectedStatus(option);
                handleFilterChange("status", option?.value);
              }}
              defaultOptions={statusOptions}
              minWidth="160px"
              isClearable={false}
            />
            <TableFilterSelect
              placeholder="Filter by sender"
              value={selectedSender}
              onChange={(option) => {
                setSelectedSender(option);
                handleFilterChange("sender_id", option?.value);
              }}
              loadOptions={loadSenderOptions}
              defaultOptions={allSenderOptions}
              isClearable={false}
            />
            <TableFilterSelect
              placeholder="Filter by recipient"
              value={selectedRecipient}
              onChange={(option) => {
                setSelectedRecipient(option);
                handleFilterChange("recipient_id", option?.value);
              }}
              loadOptions={loadRecipientOptions}
              defaultOptions={allRecipientOptions}
              isClearable={false}
            />
            <TableFilterSelect
              placeholder="Filter by stage"
              value={selectedStage}
              onChange={(option) => {
                setSelectedStage(option);
                handleFilterChange("stage_key", option?.value);
              }}
              defaultOptions={STAGE_OPTIONS}
              minWidth="160px"
              isClearable={false}
            />
          </>
        }
        activeFilters={activeFilters}
        onReset={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
      />
    </>
  );
}
